import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { api, ApiError, setAccessToken } from './api';

export interface AuthedUser {
  id: string;
  publicId: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  isSuperAdmin: boolean;
  isInternalUser: boolean;
}

interface AuthContextValue {
  user: AuthedUser | null;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  /** Returns { mfaRequired } — when true, call completeMfa with the code to finish. */
  login(email: string, password: string): Promise<{ mfaRequired: boolean; mfaToken?: string }>;
  completeMfa(mfaToken: string, code: string): Promise<void>;
  logout(): Promise<void>;
  updateUser(patch: Partial<AuthedUser>): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthedUser | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');

  // Boot: try a silent refresh — if a refresh cookie is present we'll come
  // back authenticated without the user retyping their password.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api<{ accessToken: string; user: AuthedUser }>('/auth/refresh', {
          method: 'POST',
          skipRefresh: true,
        });
        if (cancelled) return;
        setAccessToken(data.accessToken);
        setUser(data.user);
        setStatus('authenticated');
      } catch {
        if (!cancelled) setStatus('unauthenticated');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The api wrapper fires `auth:logout` if a refresh fails mid-flight.
  useEffect(() => {
    const onLogout = () => {
      setAccessToken(null);
      setUser(null);
      setStatus('unauthenticated');
    };
    window.addEventListener('auth:logout', onLogout);
    return () => window.removeEventListener('auth:logout', onLogout);
  }, []);

  // SSO callback: the SsoCallback route dispatches this after receiving
  // the access token from the API redirect.
  useEffect(() => {
    const onSsoLogin = (e: Event) => {
      const detail = (e as CustomEvent<{ accessToken: string; user: AuthedUser }>).detail;
      setAccessToken(detail.accessToken);
      setUser(detail.user);
      setStatus('authenticated');
    };
    window.addEventListener('auth:sso-login', onSsoLogin);
    return () => window.removeEventListener('auth:sso-login', onSsoLogin);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await api<{
      accessToken?: string;
      user?: AuthedUser;
      mfaRequired?: boolean;
      mfaToken?: string;
    }>('/auth/login', {
      method: 'POST',
      body: { email, password },
      skipRefresh: true,
    });
    if (data.mfaRequired) {
      return { mfaRequired: true, mfaToken: data.mfaToken };
    }
    setAccessToken(data.accessToken!);
    setUser(data.user!);
    setStatus('authenticated');
    return { mfaRequired: false };
  }, []);

  const completeMfa = useCallback(async (mfaToken: string, code: string) => {
    const data = await api<{ accessToken: string; user: AuthedUser }>('/auth/mfa/verify', {
      method: 'POST',
      body: { mfaToken, code },
      skipRefresh: true,
    });
    setAccessToken(data.accessToken);
    setUser(data.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST', skipRefresh: true });
    } catch {
      // best-effort; we always clear locally
    }
    setAccessToken(null);
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const updateUser = useCallback((patch: Partial<AuthedUser>) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, login, completeMfa, logout, updateUser }),
    [user, status, login, completeMfa, logout, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') {
    return (
      <div className="grid h-full place-items-center">
        <div className="flex flex-col items-center gap-3">
          <div className="size-7 animate-spin rounded-full border-[2.5px] border-pink-200 border-t-pink-500" />
          <p className="text-sm text-neutral-400">Loading…</p>
        </div>
      </div>
    );
  }
  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}

export function useIsSuperAdmin(): boolean {
  const { user } = useAuth();
  return user?.isSuperAdmin ?? false;
}

// Re-export so consumers can `instanceof ApiError` from one place.
export { ApiError };
